import { useId } from 'react';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import {
  workbookLookupMissingSchema,
  type WorkbookColumnarSheet,
} from '@erp/shared-types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type WorkbookLookup = WorkbookColumnarSheet['lookups'][number];
const LOOKUP_FIELDS = [
  'inputColumn',
  'matchColumn',
  'valueColumn',
  'outputColumn',
] as const;
export function WorksheetLookups({
  lookups,
  items,
  update,
  remove,
  add,
}: {
  lookups: readonly WorkbookLookup[];
  items: { id: string; name: string }[];
  update: (index: number, patch: Partial<WorkbookLookup>) => void;
  remove: (index: number) => void;
  add: (lookup: WorkbookLookup) => void;
}) {
  const gt = useGT();
  const idPrefix = useId();
  const labels = {
    // XLOOKUP's lookup_value, lookup_array and return_array, then the new column.
    inputColumn: gt('Look up value', { context: 'worksheet lookup field' }),
    matchColumn: gt('Find in column', { context: 'worksheet lookup field' }),
    valueColumn: gt('Return column', { context: 'worksheet lookup field' }),
    outputColumn: gt('New column name', { context: 'worksheet lookup field' }),
  };
  // As XLOOKUP's if_not_found: #N/A by default, or a blank, or stop the Run.
  const missingLabels: Record<WorkbookLookup['missing'], string> = {
    na: gt('#N/A', { context: 'worksheet lookup if not found option' }),
    null: gt('Empty', { context: 'worksheet lookup if not found option' }),
    error: gt('Fail the run', {
      context: 'worksheet lookup if not found option',
    }),
  };
  return (
    <div>
      <div className="mt-2 space-y-2">
        {lookups.map((lookup, index) => (
          <div
            key={index}
            className="grid grid-cols-2 gap-2 rounded bg-muted/40 p-2"
          >
            <Select
              value={lookup.sourceId || null}
              onValueChange={(sourceId) => update(index, { sourceId })}
            >
              <SelectTrigger
                className="w-full"
                aria-label={gt('Lookup source', {
                  context: 'worksheet lookup source',
                })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {items.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {LOOKUP_FIELDS.map((key) => (
              <Input
                key={key}
                value={lookup[key]}
                aria-label={labels[key]}
                placeholder={labels[key]}
                onChange={(event) =>
                  update(index, { [key]: event.target.value })
                }
              />
            ))}
            <div className="flex items-center gap-2 text-xs">
              <span id={`${idPrefix}-missing-${index}`}>
                <T context="worksheet lookup: what a value with no match gives">
                  If not found
                </T>
              </span>
              <Select
                value={lookup.missing}
                onValueChange={(value) =>
                  update(index, {
                    missing: workbookLookupMissingSchema.parse(value),
                  })
                }
              >
                <SelectTrigger
                  size="sm"
                  aria-labelledby={`${idPrefix}-missing-${index}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {workbookLookupMissingSchema.options.map((missing) => (
                    <SelectItem key={missing} value={missing}>
                      {missingLabels[missing]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove(index)}>
              <TrashIcon />
              <T context="worksheet remove lookup">Remove lookup</T>
            </Button>
          </div>
        ))}
        <Button
          size="sm"
          variant="outline"
          disabled={!items.length || lookups.length >= 10}
          onClick={() => {
            const source = items[0];
            if (source)
              add({
                sourceId: source.id,
                inputColumn: '',
                matchColumn: '',
                valueColumn: '',
                outputColumn: '',
                missing: 'na',
              });
          }}
        >
          <PlusIcon />
          <T context="worksheet add lookup">Add lookup</T>
        </Button>
      </div>
    </div>
  );
}
